import { search } from "fast-fuzzy";
import { collection, onSnapshot } from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import { exportFiles } from "../Services/api";
import {
  setLocationSummaryData,
  setLocationsData,
  setLocationsResults,
  setZoneAndWardData
} from "../Redux/locations/action";
import { setErrorStatus } from "../Redux/status/action";
import { setTablePageNumber } from "../Redux/table/action";
import { database } from "../Services/firebase";
import { isValidArray, isValidObject } from "../Services/validators";
import {
  calculateTotal,
  calculateTotalPercentage,
  dateAndTimeConverter,
  extractNumbers,
  getCaptureSummaryPercentage,
  getComplaintsCount,
  getCompletedProcedureCount,
  getLastManPowerCaptured,
  getStatusCount,
  getUniqueDataFromObj,
  getValidStatusByCount,
  isFilterApplied
} from "../Utils/Constants/Constants";
import { locationTableField } from "../Utils/Constants/Locations";

export default function useLocations(props) {
  const locationsListener = useRef(null);
  const locationData = useRef({});
  const locationResults = useRef({});
  const pageNumber = useRef(1);
  const sortChanged = useRef(false);
  const [locationsLoading, setLocationsLoading] = useState({
      export: false
    });

  useEffect(() => {
    sortChanged.current = true;
  }, [props.sort]);

  const subscribeToLocations = () => {
    const collectionRef = collection(database, "locations");
    let locations = {};
    return onSnapshot(
      collectionRef,
      (dataSnapshot) => {
        dataSnapshot.forEach((doc) => {
          if (!doc.data().actions?.deletedAt) {
            locations[doc.id] = {
              ...doc.data(),
              documentId: doc.id
            };
          }
        });

        const updatedLocationData = calculateLocationData(
          locations,
          props.captureSummaryData
        );
        locationData.current = updatedLocationData;
        setLocationsData(updatedLocationData);

        setZoneAndWardData({
          zones: getUniqueDataFromObj("zone", locations).sort((a, b) => a - b),
          wards: getUniqueDataFromObj("ward", locations).sort((a, b) =>
            a.localeCompare(b)
          )
        });
      },
      (error) => {
        console.error(
          { isAuth: props.isAuth },
          error,
          "from locations listener"
        );
        setErrorStatus(error);
      }
    );
  };

  useEffect(() => {
    const processData = () => {
      if (!locationData.current || !isValidObject(locationData.current)) {
        return [];
      }

      let data = Object.values(locationData.current);

      if (props.filterData || props.captureSummaryData) {
        data = filterLocations(
          locationData.current,
          props.filterData,
          props.captureSummaryData
        );
      }

      if (isValidArray(data) && props.searchKey) {
        data = search(props.searchKey, data, {
          keySelector: (item) => `${item.location} ${item.PCTNo}`,
          threshold: 0.8
        });
      }

      if (isValidArray(data) && props.sort && !props.searchKey) {
        data = sortedLocations(data, props.sort);
      }

      return data;
    };

    if (sortChanged.current) {
      pageNumber.current = 1;
      setTablePageNumber("LOCATIONS", pageNumber.current);
      sortChanged.current = false;
    }

    const data = processData();

    const modifiedData = isValidArray(data)
      ? data.reduce((acc, item) => {
          acc[item.documentId] = item;
          return acc;
        }, {})
      : {};

    locationResults.current = modifiedData;
    const startIndex = (pageNumber.current - 1) * 50;
    const endIndex = startIndex + 50;

    setLocationsResults(
      Object.values(modifiedData).slice(startIndex, endIndex),
      Object.values(modifiedData).length
    );
    // eslint-disable-next-line
  }, [
    props.captureSummaryData,
    props.filterData,
    props.searchKey,
    props.sort,
    locationData.current
  ]);

  const getLocationNextPage = () => {
    const data = Object.values(locationResults.current);
    pageNumber.current += 1;
    setTablePageNumber("LOCATIONS", pageNumber.current);
    setLocationsResults(
      handlePagination(data, pageNumber.current),
      data.length
    );
  };

  const getLocationPreviousPage = () => {
    const data = Object.values(locationResults.current);
    pageNumber.current -= 1;
    setTablePageNumber("LOCATIONS", pageNumber.current);
    setLocationsResults(
      handlePagination(data, pageNumber.current),
      data.length
    );
  };

  const getLocationsSummary = async () => {
    await downloadLocationsSummary({
      accessToken: props.accessToken,
      filters: buildLocationsFilterQuery(props.filterData),
      locationsLoading: locationsLoading,
      setLocationsLoading: setLocationsLoading
    });
  };

  const locationsFilterFieldMap = {
  category: "category",
  zone: "zone",
  ward: "ward",
  status: "operationalStatus.state"
};

const buildLocationsFilterQuery = (locationFilter) => {
  if (!isFilterApplied(locationFilter, "LOCATIONS").validation) {
    return null;
  }

  const triples = [];
  Object.entries(locationsFilterFieldMap).forEach(([filterKey, field]) => {
    const values = locationFilter?.[filterKey];
    if (Array.isArray(values) && values.length > 0) {
      triples.push(field, "in", values);
    }
  });
  return triples.length > 0 ? triples : null;
};

  useEffect(() => {
    if (props.isAuth && locationsListener.current === null) {
      locationsListener.current = subscribeToLocations();
    } else if (
      props.isAuth === false &&
      typeof locationsListener.listener === "function"
    ) {
      locationsListener.current();
      locationsListener.current = null;
    }
    // eslint-disable-next-line
  }, [props.isAuth]);

  return {
    getLocationNextPage, 
    getLocationPreviousPage, 
    getLocationsSummary, 
    locationsLoading 
  };
}

const downloadLocationsSummary = async (props) => {
  try {
    props.setLocationsLoading({ ...props.locationsLoading, export: true });

    const blob = await exportFiles(props.accessToken, props.filters, "locations");

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;

    link.setAttribute(
      "download",
      `Locations Report @${dateAndTimeConverter(Date.now(), "Time").replace(
        ":",
        "-"
      )},
        ${dateAndTimeConverter(Date.now(), "cardDate").replace(":", "-")}.xlsx`
    );
    document.body.appendChild(link);
    link.click();

    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    props.setLocationsLoading({ ...props.locationsLoading, export: false });
  } catch (error) {
    props.setLocationsLoading({ ...props.locationsLoading, export: false });
    setErrorStatus(error);
    console.error("There was an error downloading the file:", error);
  }
};

const filterLocations = (locations, locationFilter, captureSummaryData) => {
  let updatedData = [];
  const locationsData = Object.values(locations);
  let lastManpowerCapturedArray = [];
  let manPowerPercentageArray = [];

  const today = new Date().toISOString().split("T")[0];
  if (isFilterApplied(locationFilter, "LOCATIONS").validation) {
    const _data = locationsData?.filter((item) => {
      // Category filter
      if (
        locationFilter?.category?.length > 0 &&
        !locationFilter?.category.includes(item.category)
      ) {
        return false;
      }

      // Zone filter
      if (
        locationFilter?.zone?.length > 0 &&
        !locationFilter?.zone.includes(item.zone)
      ) {
        return false;
      }

      // Ward filter
      if (
        locationFilter?.ward?.length > 0 &&
        !locationFilter?.ward.includes(item.ward)
      ) {
        return false;
      }

      // Status filter
      if (
        locationFilter?.status?.length > 0 &&
        !locationFilter?.status.includes(item?.operationalStatus?.state)
      ) {
        return false;
      }
      // Date  filter
      if (
        locationFilter &&
        ((locationFilter?.dateRange?.from === null &&
          locationFilter?.dateRange?.to === null) ||
          locationFilter?.dateRange?.from !== today ||
          locationFilter?.dateRange?.to !== today)
      ) {
        const { documentId, complaintCount, procedureCount } = item;
        const captureSummary = captureSummaryData?.[documentId];

        item.totalManPower = captureSummary
          ? calculateTotalPercentage(
              captureSummary,
              locationFilter?.dateRange?.from,
              locationFilter?.dateRange?.to,
              "dayCount"
            )
          : "N/A";

        manPowerPercentageArray.push(item.totalManPower);

        item.totalManPowerCapacity = captureSummary
          ? getLastManPowerCaptured(captureSummary)
          : "N/A";

        lastManpowerCapturedArray.push(item.totalManPowerCapacity ? 1 : 0);

        item.totalOpenCompliant = complaintCount
          ? calculateTotal(
              item,
              locationFilter?.dateRange?.from,
              locationFilter?.dateRange?.to,
              "complaintCount",
              "open"
            )
          : 0;
        item.totalClosedCompliant = complaintCount
          ? calculateTotal(
              item,
              locationFilter?.dateRange?.from,
              locationFilter?.dateRange?.to,
              "complaintCount",
              "closed"
            )
          : 0;

        item.totalUnderReviewCompliant = complaintCount
          ? calculateTotal(
              item,
              locationFilter?.dateRange?.from,
              locationFilter?.dateRange?.to,
              "complaintCount",
              "underReview"
            )
          : 0;

        item.totalPositiveProcedure =
          procedureCount &&
          locationFilter?.dateRange?.from !== locationFilter?.dateRange?.to
            ? calculateTotal(
                item,
                locationFilter?.dateRange?.from,
                locationFilter?.dateRange?.to,
                "procedureCount",
                "positive"
              )
            : 0;
        item.totalNegativeProcedure = procedureCount
          ? calculateTotal(
              item,
              locationFilter?.dateRange?.from,
              locationFilter?.dateRange?.to,
              "procedureCount",
              "negative"
            )
          : 0;
        item.totalCompletedProcedure =
          procedureCount &&
          calculateTotal(
            item,
            locationFilter?.dateRange?.from,
            locationFilter?.dateRange?.to,
            "procedureCount",
            "positive"
          ) +
            calculateTotal(
              item,
              locationFilter?.dateRange?.from,
              locationFilter?.dateRange?.to,
              "procedureCount",
              "negative"
            );

        item.status = getValidStatusByCount(
          item?.complaintCount
            ? calculateTotal(
                item,
                locationFilter?.dateRange?.from,
                locationFilter?.dateRange?.to,
                "complaintCount",
                "open"
              ) -
                calculateTotal(
                  item,
                  locationFilter?.dateRange?.from,
                  locationFilter?.dateRange?.to,
                  "complaintCount",
                  "closed"
                )
            : 0
        );
      }

      item.operationalStatusState = item?.operationalStatus?.state;
      return true;
    });

    updatedData = _data;
  } else {
    // eslint-disable-next-line no-unused-vars
    for (const [_key, item] of Object.entries(locations)) {
      const { documentId, complaintCount, procedureCount } = item;
      const captureSummary = captureSummaryData?.[documentId];

      const totalManPowerCapacity = captureSummary
        ? getLastManPowerCaptured(captureSummary)
        : "N/A";

      lastManpowerCapturedArray.push(totalManPowerCapacity ? 1 : 0);

      const totalManPower = captureSummary
        ? getCaptureSummaryPercentage(captureSummary)
        : "N/A";

      manPowerPercentageArray.push(totalManPower);

      const totalOpenCompliant = complaintCount
        ? getComplaintsCount(item, "open")
        : 0;

      const totalClosedCompliant = complaintCount
        ? getComplaintsCount(item, "closed")
        : 0;

      const totalUnderReviewCompliant = complaintCount
        ? getComplaintsCount(item, "underReview")
        : 0;

      const totalPositiveProcedure = procedureCount
        ? getCompletedProcedureCount(item)
        : 0;
      const totalNegativeProcedure = procedureCount
        ? getCompletedProcedureCount(item)
        : 0;
      const totalCompletedProcedure = procedureCount
        ? getCompletedProcedureCount(item)
        : 0;

      const status = getValidStatusByCount(getStatusCount(item));

      updatedData.push({
        ...item,
        totalManPowerCapacity,
        totalManPower,
        totalOpenCompliant,
        totalClosedCompliant,
        totalUnderReviewCompliant,
        totalPositiveProcedure,
        totalNegativeProcedure,
        totalCompletedProcedure,
        status,
        operationalStatusState: item?.operationalStatus?.state  
      });
    }
  }

  const _manPowerTotalPercentage = manPowerPercentageArray.filter(
    (value) => value !== "N/A"
  );
  const manPowerAverage =
    _manPowerTotalPercentage.reduce((a, b) => a + b, 0) /
      _manPowerTotalPercentage.length || 0;

  const _lastManpowerCapturePercentage = lastManpowerCapturedArray.filter(
    (value) => value !== "N/A"
  );
  const lastManpowerCapturedAverage =
    _lastManpowerCapturePercentage.reduce((a, b) => a + b, 0) /
      _lastManpowerCapturePercentage.length || 0;

  setLocationSummaryData(
    manPowerAverage.toFixed(2),
    lastManpowerCapturedAverage.toFixed(2)
  );

  return updatedData;
};

const sortedLocations = (data, sort) => {
  return data?.sort((a, b) => {
    let fieldA, fieldB;

    switch (sort?.title) {
      case "STATUS":
         fieldA = a?.operationalStatusState;
         fieldB = b?.operationalStatusState;
        break;
      case "WARD":
        fieldA = extractNumbers(a?.ward);
        fieldB = extractNumbers(b?.ward);
        break;
      case "PCT #":
        fieldA = String(a?.PCTNo);
        fieldB = String(b?.PCTNo);
        break;
      default:
        fieldA = a?.[locationTableField(sort.title)];
        fieldB = b?.[locationTableField(sort.title)];
        break;
    }

    if (["PCT #"].includes(sort.title)) {
      return sort.ascending
        ? fieldA.localeCompare(fieldB, undefined, { numeric: true })
        : fieldB.localeCompare(fieldA, undefined, { numeric: true });
    }

    if (
      [
        "WARD",
        "ZONE",
        "RESOLVED COMP.S",
        "UNRESOLVED COMP.S",
        "COMPLETED PROCEDURES",
        "MANPOWER",
        "LAST EMP. CAP."
      ].includes(sort.title)
    ) {
      if (typeof fieldA === "boolean" && typeof fieldB === "boolean") {
        return sort.ascending
          ? Number(fieldA) - Number(fieldB)
          : Number(fieldB) - Number(fieldA);
      } else if (!isNaN(fieldA) && !isNaN(fieldB)) {
        return sort.ascending ? fieldA - fieldB : fieldB - fieldA;
      } else if (fieldA === "N/A" || fieldB === "N/A") {
        if (sort.ascending) {
          return fieldA === "N/A" ? -1 : 1;
        } else {
          return fieldA === "N/A" ? 1 : -1;
        }
      }
    } else {
      const stringA = String(fieldA).toLowerCase();
      const stringB = String(fieldB).toLowerCase();
      if (stringA < stringB) {
        return sort.ascending ? -1 : 1;
      }
      if (stringA > stringB) {
        return sort.ascending ? 1 : -1;
      }
    }

    return 0;
  });
};

function calculateLocationData(locations, captureSummaryData) {
  let updatedData = {};
  let lastManpowerCapturedArray = [];
  let manPowerPercentageArray = [];
  for (const [key, item] of Object.entries(locations)) {
    const { documentId, complaintCount, procedureCount } = item;
    const captureSummary = captureSummaryData?.[documentId];

    const totalManPowerCapacity = captureSummary
      ? getLastManPowerCaptured(captureSummary)
      : "N/A";

    lastManpowerCapturedArray.push(totalManPowerCapacity ? 1 : 0);

    const totalManPower = captureSummary
      ? getCaptureSummaryPercentage(captureSummary)
      : "N/A";

    manPowerPercentageArray.push(totalManPower);

    const totalOpenCompliant = complaintCount
      ? getComplaintsCount(item, "open")
      : 0;

    const totalClosedCompliant = complaintCount
      ? getComplaintsCount(item, "closed")
      : 0;

    const totalUnderReviewCompliant = complaintCount
      ? getComplaintsCount(item, "underReview")
      : 0;

    const totalPositiveProcedure = procedureCount
      ? getCompletedProcedureCount(item)
      : 0;
    const totalNegativeProcedure = procedureCount
      ? getCompletedProcedureCount(item)
      : 0;
    const totalCompletedProcedure = procedureCount
      ? getCompletedProcedureCount(item)
      : 0;

    const status = getValidStatusByCount(getStatusCount(item));

    updatedData[key] = {
      ...item,
      totalManPowerCapacity,
      totalManPower,
      totalOpenCompliant,
      totalClosedCompliant,
      totalUnderReviewCompliant,
      totalPositiveProcedure,
      totalNegativeProcedure,
      totalCompletedProcedure,
      status,
      operationalStatusState: item?.operationalStatus?.state  
    };
  }

  const _manPowerTotalPercentage = manPowerPercentageArray.filter(
    (value) => value !== "N/A"
  );
  const manPowerAverage =
    _manPowerTotalPercentage.reduce((a, b) => a + b, 0) /
      _manPowerTotalPercentage.length || 0;

  const _lastManpowerCapturePercentage = lastManpowerCapturedArray.filter(
    (value) => value !== "N/A"
  );
  const lastManpowerCapturedAverage =
    _lastManpowerCapturePercentage.reduce((a, b) => a + b, 0) /
      _lastManpowerCapturePercentage.length || 0;

  setLocationSummaryData(
    manPowerAverage.toFixed(2),
    lastManpowerCapturedAverage.toFixed(2)
  );
  return updatedData;
}

const handlePagination = (data, currentPage) => {
  const start = (currentPage - 1) * 50;
  const end = currentPage * 50;
  return data.slice(start, end);
};
